<script setup lang="ts">
// Игра «Охотник на контракты» (змейка) в сайдбаре AI-ассистента (ChatFab.vue): открывается,
// когда ассистент начинает отвечать, и встаёт на паузу, когда он закончил (active → false).
// Сама игра — public/games/snake-contracts.html?embed=1 (iframe, общение через postMessage).
// Результаты партий — у каждого пользователя свои: /api/games/snake (server/src/routes/games.ts).
const props = defineProps<{
  active: boolean
  visible: boolean
  // Вопрос ассистента с вариантами — игра встаёт на паузу и показывает его поверх поля
  question?: { text: string; options: string[] } | null
}>()
const emit = defineEmits<{ close: []; answer: [option: string] }>()

interface GameStats { best: number; games_played: number }

const frame = ref<HTMLIFrameElement | null>(null)
const stats = ref<GameStats | null>(null)
const src = `${useRuntimeConfig().app.baseURL}games/snake-contracts.html?embed=1`

function post(msg: Record<string, unknown>) {
  frame.value?.contentWindow?.postMessage(msg, window.location.origin)
}

function sendStats() {
  if (stats.value) post({ type: 'snake:stats', best: stats.value.best })
}

async function loadStats() {
  try {
    stats.value = await useApi<GameStats>('/games/snake/stats')
    sendStats()
  } catch { /* без рекорда игра всё равно работает */ }
}

// Сессия партии: сервер выдаёт зерно, по нему игра строит поле; в конце уходят только ходы,
// счёт сервер считает сам (server/src/routes/games.ts) — подделать его из браузера нельзя.
async function newSession() {
  try {
    const s = await useApi<{ session_id: string; seed: number }>('/games/snake/sessions', { method: 'POST' })
    post({ type: 'snake:session', session_id: s.session_id, seed: s.seed })
  } catch { /* без сессии партия идёт, но не засчитывается */ }
}

async function saveResult(data: { session_id: string; rows: number; ticks: number; events: [number, number][] }) {
  try {
    stats.value = await useApi<GameStats>('/games/snake/results', {
      method: 'POST',
      body: { session_id: data.session_id, rows: data.rows, ticks: data.ticks, events: data.events },
    })
    sendStats()
  } catch (e) {
    console.error('Failed to save game result:', e)
  }
}

function onMessage(e: MessageEvent) {
  if (e.origin !== window.location.origin || e.source !== frame.value?.contentWindow || !e.data) return
  const { type } = e.data
  if (type === 'snake:ready') { loadStats(); sendQuestion() }
  else if (type === 'snake:new-session') newSession()
  else if (type === 'snake:answer' && typeof e.data.option === 'string') emit('answer', e.data.option)
  else if (type === 'snake:result') saveResult(e.data)
}

// Ассистент закончил ответ — игра на паузу
watch(() => props.active, (active) => {
  if (!active) post({ type: 'snake:pause', reason: 'assistant' })
})
// Игру скрыли (крестик / кнопка в шапке) — тоже пауза, иначе змейка бегает невидимо
watch(() => props.visible, (visible) => {
  if (!visible) post({ type: 'snake:pause', reason: 'hidden' })
})

function sendQuestion() {
  const q = props.question
  if (q?.text) post({ type: 'snake:question', text: q.text, options: [...q.options] })
  else post({ type: 'snake:question-clear' })
}
watch(() => props.question, sendQuestion)

onMounted(() => window.addEventListener('message', onMessage))
onUnmounted(() => window.removeEventListener('message', onMessage))
</script>

<template>
  <!-- Занимает всю область, которую задаёт родитель (в ChatFab — всё тело чата между шапкой
       и полем ввода); поле игры подстраивает сетку под эту площадь. -->
  <div class="flex flex-col bg-white dark:bg-gray-900">
    <div class="flex flex-none items-center justify-between gap-2 px-3 py-1.5">
      <div class="flex min-w-0 items-center gap-2 text-micro text-gray-500 dark:text-gray-400">
        <Icon name="lucide:gamepad-2" class="h-3.5 w-3.5 flex-none text-primary-500" />
        <span class="truncate">
          <template v-if="active">Пока ассистент отвечает</template>
          <template v-else>Охотник на контракты</template>
          <template v-if="stats"> · рекорд {{ stats.best.toLocaleString('ru-RU') }} · партий {{ stats.games_played }}</template>
        </span>
      </div>
      <UButton
        icon="lucide:x"
        color="neutral"
        variant="ghost"
        size="xs"
        aria-label="Скрыть игру"
        title="Скрыть игру"
        @click="emit('close')"
      />
    </div>
    <div class="min-h-0 flex-1">
      <iframe
        ref="frame"
        :src="src"
        title="Охотник на контракты"
        class="block h-full w-full border-0"
      />
    </div>
  </div>
</template>
